import {
    type ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    type Draft,
} from '@reduxjs/toolkit';
import type { IAction, SlicePropsWithData } from './initStore';
import { backendApiUrl } from '../App';

const initialState: ISessionState = {
    isExpired: false,
    lastRequest: Date.now(),
    data: null,
    loading: false,
    error: null,
};

const thunks = {
    getSessionState: async (): Promise<ISessionCheckResult> => {
        try {
            const response = await fetch(backendApiUrl.session.check);

            if (!response.ok) {
                return Promise.reject(new Error(await response.text()));
            }

            return (await response.json()) as ISessionCheckResult;
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },
    updateSessionState: async (): Promise<ISessionCheckResult> => {
        try {
            const response = await fetch(backendApiUrl.session.update, {
                method: 'POST',
            });

            if (!response.ok) {
                return Promise.reject(new Error(await response.text()));
            }

            return (await response.json()) as ISessionCheckResult;
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    _reducers: (builder: ActionReducerMapBuilder<ISessionState>) => {
        builder.addCase(getSessionState.pending, (state: Draft<ISessionState>) => {
            state.lastRequest = Date.now();
            state.loading = true;
            state.error = null;
        });
        builder.addCase(
            getSessionState.fulfilled,
            (state: Draft<ISessionState>, action: IAction<ISessionCheckResult>) => {
                state.isExpired = false;
                state.data = action.payload;
                state.loading = false;
                state.error = null;
            },
        );
        builder.addCase(getSessionState.rejected, (state: Draft<ISessionState>, action) => {
            state.isExpired = true;
            state.loading = false;
            state.error = action.error ?? null;
        });

        builder.addCase(updateSessionState.pending, (state: Draft<ISessionState>) => {
            state.lastRequest = Date.now();
            state.loading = true;
            state.error = null;
        });
        builder.addCase(
            updateSessionState.fulfilled,
            (state: Draft<ISessionState>, action: IAction<ISessionCheckResult>) => {
                state.isExpired = false;
                state.data = action.payload;
                state.loading = false;
                state.error = null;
            },
        );
        builder.addCase(updateSessionState.rejected, (state: Draft<ISessionState>, action) => {
            state.isExpired = true;
            state.loading = false;
            state.error = action.error ?? null;
        });
    },
};

export const getSessionState = createAsyncThunk('session/check', thunks.getSessionState);
export const updateSessionState = createAsyncThunk('session/update', thunks.updateSessionState);

export const sessionSlice = createSlice({
    name: 'session',
    initialState,
    extraReducers: thunks._reducers,
    reducers: {},
});

interface ISessionCheckResult {
    /** Timestamp when the session will expire. */
    expirationTime: number;
}

export interface ISessionState extends SlicePropsWithData<ISessionCheckResult | null> {
    /** Wether the session is already expired. */
    isExpired: boolean;

    /** Timestamp when the last request was sent. */
    lastRequest: number;
}

export default sessionSlice.reducer;
